import { useState, type ComponentProps } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import type { Account } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { useOnboardingData } from '@/hooks/useOnboardingData'
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Eye,
  Trash2,
  Database,
  ClipboardCheck,
  Rocket,
  Clock,
} from 'lucide-react'
import { AddAccountDialog } from '@/components/accounts/AddAccountDialog'
import { LoadSampleDataDialog } from '@/components/accounts/LoadSampleDataDialog'
import { OnboardingProgressCell } from '@/components/accounts/OnboardingProgressCell'
import { OnboardingProgressDialog } from '@/components/accounts/OnboardingProgressDialog'

const SHOW_SAMPLE_DATA = import.meta.env.VITE_SHOW_SAMPLE_DATA === 'true'

// `is_live` is added by migration 20260622000000_add_is_live_to_accounts.sql.
// Intersect locally until the generated @limo/database types are regenerated
// (npm run db:types); the cast is harmless once `is_live` lands in Account.
type AccountWithLive = Account & { is_live: boolean }

type OnboardingData = ComponentProps<typeof OnboardingProgressCell>['data']

export function AccountsListPage() {
  const [search, setSearch] = useState('')
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [sampleDataAccount, setSampleDataAccount] = useState<AccountWithLive | null>(null)
  const [onboardingAccount, setOnboardingAccount] = useState<AccountWithLive | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { startImpersonation } = useImpersonation()

  const { data: onboardingData } = useOnboardingData()

  const { data: accounts, isLoading } = useQuery({
    queryKey: ['accounts', search],
    queryFn: async () => {
      let query = supabase
        .from('accounts')
        .select('*')
        .neq('status', 'deleted')
        .order('created_at', { ascending: false })

      if (search) {
        query = query.or(`name.ilike.%${search}%,slug.ilike.%${search}%`)
      }

      const { data, error } = await query
      if (error) throw error
      return data as AccountWithLive[]
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Soft delete - set status to 'deleted'
      const { error } = await supabase
        .from('accounts')
        .update({ status: 'deleted' })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      toast({
        title: 'Account deleted',
        description: 'The account has been successfully deleted.',
      })
      setDeleteId(null)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const toggleLiveMutation = useMutation({
    mutationFn: async ({ id, isLive }: { id: string; isLive: boolean }) => {
      // `is_live` isn't in the generated types yet (see AccountWithLive above),
      // so route this write through a minimal cast — same pattern the qualifier
      // hooks use for not-yet-typed columns.
      const { error } = await (
        supabase.from('accounts') as unknown as {
          update: (v: Record<string, unknown>) => {
            eq: (col: string, val: string) => Promise<{ error: { message: string } | null }>
          }
        }
      )
        .update({ is_live: isLive })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      toast({
        title: variables.isLive ? 'Account marked live' : 'Account moved to pending',
      })
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  const handleImpersonate = (account: AccountWithLive) => {
    startImpersonation(account.slug)
    navigate(`/admin/accounts/${account.slug}/view/dashboard`)
  }

  // Otto-only operators live in their own table and never mix into the
  // Booked Rides live/pending tables — they have no BR site to take live.
  const ottoAccounts = accounts?.filter((a) => a.account_type === 'miles_only') ?? []
  const fullAccounts = accounts?.filter((a) => a.account_type !== 'miles_only') ?? []
  const liveAccounts = fullAccounts.filter((a) => a.is_live)
  const pendingAccounts = fullAccounts.filter((a) => !a.is_live)

  const rowHandlers: AccountRowHandlers = {
    onImpersonate: handleImpersonate,
    onEdit: (account) => navigate(`/admin/accounts/${account.id}`),
    onOnboarding: setOnboardingAccount,
    onSampleData: setSampleDataAccount,
    onToggleLive: (account) =>
      toggleLiveMutation.mutate({ id: account.id, isLive: !account.is_live }),
    onDelete: setDeleteId,
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Accounts</h1>
          <p className="text-muted-foreground">
            Manage limo company accounts on the platform
          </p>
        </div>
        <Button onClick={() => setAddDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Account
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search accounts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <AccountsTable
        title={`Live Accounts${isLoading ? '' : ` (${liveAccounts.length})`}`}
        description="Accounts marked as 100% live."
        accounts={liveAccounts}
        isLoading={isLoading}
        emptyMessage="No live accounts yet."
        onboardingData={onboardingData}
        handlers={rowHandlers}
      />

      <AccountsTable
        title={`Pending Accounts${isLoading ? '' : ` (${pendingAccounts.length})`}`}
        description="Accounts still being set up. New accounts start here."
        accounts={pendingAccounts}
        isLoading={isLoading}
        emptyMessage="No pending accounts."
        onboardingData={onboardingData}
        handlers={rowHandlers}
      />

      <AccountsTable
        title={`Otto AI Accounts${isLoading ? '' : ` (${ottoAccounts.length})`}`}
        description="Otto AI portal-only operator accounts. Not shown in the Booked Rides tables above."
        accounts={ottoAccounts}
        isLoading={isLoading}
        emptyMessage="No Otto AI accounts yet."
        onboardingData={onboardingData}
        handlers={rowHandlers}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will soft-delete the account. The account and its data will
              be marked as deleted but can be restored if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AddAccountDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
      />

      <LoadSampleDataDialog
        account={sampleDataAccount}
        open={!!sampleDataAccount}
        onOpenChange={(open) => !open && setSampleDataAccount(null)}
      />

      <OnboardingProgressDialog
        account={onboardingAccount}
        data={onboardingData}
        open={!!onboardingAccount}
        onOpenChange={(open) => !open && setOnboardingAccount(null)}
      />
    </div>
  )
}

interface AccountRowHandlers {
  onImpersonate: (account: AccountWithLive) => void
  onEdit: (account: AccountWithLive) => void
  onOnboarding: (account: AccountWithLive) => void
  onSampleData: (account: AccountWithLive) => void
  onToggleLive: (account: AccountWithLive) => void
  onDelete: (id: string) => void
}

interface AccountsTableProps {
  title: string
  description: string
  accounts: AccountWithLive[]
  isLoading: boolean
  emptyMessage: string
  onboardingData: OnboardingData
  handlers: AccountRowHandlers
}

function AccountsTable({
  title,
  description,
  accounts,
  isLoading,
  emptyMessage,
  onboardingData,
  handlers,
}: AccountsTableProps) {
  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="px-0 sm:px-6">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Onboarding</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-2 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-8 w-8" /></TableCell>
                  </TableRow>
                ))
              ) : accounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell
                      className="font-medium cursor-pointer hover:underline"
                      onClick={() => handlers.onImpersonate(account)}
                    >
                      {account.name}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {account.slug}
                    </TableCell>
                    <TableCell>
                      <StatusText status={account.status} />
                    </TableCell>
                    <TableCell>
                      <OnboardingProgressCell
                        account={account}
                        data={onboardingData}
                        onClick={() => handlers.onOnboarding(account)}
                      />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(account.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handlers.onEdit(account)}
                            className="cursor-pointer"
                          >
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handlers.onImpersonate(account)}
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View as Account
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handlers.onOnboarding(account)}
                          >
                            <ClipboardCheck className="mr-2 h-4 w-4" />
                            Onboarding Progress
                          </DropdownMenuItem>
                          {SHOW_SAMPLE_DATA && (
                            <DropdownMenuItem
                              onClick={() => handlers.onSampleData(account)}
                            >
                              <Database className="mr-2 h-4 w-4" />
                              Load Sample Data
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handlers.onToggleLive(account)}
                            className="cursor-pointer"
                          >
                            {account.is_live ? (
                              <>
                                <Clock className="mr-2 h-4 w-4" />
                                Move to Pending
                              </>
                            ) : (
                              <>
                                <Rocket className="mr-2 h-4 w-4" />
                                Mark as Live
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handlers.onDelete(account.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

function StatusText({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'text-green-600',
    suspended: 'text-red-600',
    deleted: 'text-muted-foreground',
  }

  return (
    <span className={`text-sm font-medium capitalize ${colors[status] || 'text-muted-foreground'}`}>
      {status}
    </span>
  )
}
